import React from 'react';
import {
  Users,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Building2,
  Mail,
  Globe,
  RefreshCw,
  Shield,
  Info
} from 'lucide-react';

const AccountSwitchingDocs: React.FC = () => {
  return (
    <div className="max-w-5xl mx-auto p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-3 rounded-lg">
            <Users className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Account Switching</h1>
            <p className="text-gray-600 dark:text-gray-400">Switch between multiple accounts using the same email address</p>
          </div>
        </div>
      </div>

      {/* Overview */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Info className="h-6 w-6 mr-2 text-purple-600" />
          Overview
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            If your email is associated with multiple client accounts, the Account Switching feature allows you to
            seamlessly switch between them without logging out. This is particularly useful for:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 ml-4">
            <li>Consultants managing multiple client organizations</li>
            <li>Agencies with separate accounts for each client</li>
            <li>Users who work across multiple business units</li>
            <li>Teams using the same email for different departments</li>
          </ul>
        </div>
      </section>

      {/* How to Access */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Globe className="h-6 w-6 mr-2 text-purple-600" />
          Accessing Account Switching
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="bg-purple-100 dark:bg-purple-900/20 rounded-full p-2 mt-1">
                <CheckCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Step 1: Navigate to Settings</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Click on your profile icon in the top right corner and select "Settings" from the dropdown menu.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-purple-100 dark:bg-purple-900/20 rounded-full p-2 mt-1">
                <CheckCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Step 2: Go to Accounts</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  In the settings sidebar, click on "Accounts" to view all accounts associated with your email.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-purple-100 dark:bg-purple-900/20 rounded-full p-2 mt-1">
                <CheckCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Step 3: View Available Accounts</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  You'll see a list of all client accounts linked to your email address, displayed as cards with
                  company information.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Account Card Information */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Building2 className="h-6 w-6 mr-2 text-purple-600" />
          Understanding Account Cards
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <p className="text-gray-700 dark:text-gray-300 mb-6">
            Each account is displayed as a card showing key information:
          </p>

          <div className="space-y-6">
            {/* Current Account Indicator */}
            <div className="border-l-4 border-green-500 bg-green-50 dark:bg-green-900/10 p-4 rounded-r-lg">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Current Account Badge</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    The account you're currently using will display a green "Current Account" badge at the top of the card.
                  </p>
                </div>
              </div>
            </div>

            {/* Active/Inactive Status */}
            <div className="border-l-4 border-blue-500 bg-green-50 dark:bg-blue-900/10 p-4 rounded-r-lg">
              <div className="flex items-start space-x-3">
                <Shield className="h-5 w-5 text-brandColor mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Account Status</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm mb-2">
                    Each card shows the account status:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 text-sm ml-4">
                    <li><span className="font-medium text-green-600">Active</span> - Account is operational and ready to use</li>
                    <li><span className="font-medium text-red-600">Inactive</span> - Account has been deactivated</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="border-l-4 border-purple-500 bg-purple-50 dark:bg-purple-900/10 p-4 rounded-r-lg">
              <div className="flex items-start space-x-3">
                <Building2 className="h-5 w-5 text-purple-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Company Details</h4>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Each card displays the company name, email, and phone number for easy identification.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Switch */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <RefreshCw className="h-6 w-6 mr-2 text-purple-600" />
          Switching Between Accounts
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-purple-100 dark:bg-purple-900/20 rounded-full p-2 mt-1 flex-shrink-0">
                <span className="text-purple-600 font-bold">1</span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Locate the Account</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Find the account card you want to switch to from the list of available accounts.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <ArrowRight className="h-6 w-6 text-gray-400" />
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-purple-100 dark:bg-purple-900/20 rounded-full p-2 mt-1 flex-shrink-0">
                <span className="text-purple-600 font-bold">2</span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Click Switch Account</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Click the "Switch Account" button on the desired account card. The button will show a loading state
                  while processing.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <ArrowRight className="h-6 w-6 text-gray-400" />
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-purple-100 dark:bg-purple-900/20 rounded-full p-2 mt-1 flex-shrink-0">
                <span className="text-purple-600 font-bold">3</span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Automatic Redirect</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  After successfully switching, you'll be automatically redirected to the dashboard of the new account.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-gray-700 dark:text-gray-300">
                <p className="font-medium text-purple-900 dark:text-purple-300 mb-1">Quick Tip</p>
                <p>
                  You can switch between accounts as many times as needed. Your session and authentication
                  remain valid across all accounts linked to your email.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Details */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Shield className="h-6 w-6 mr-2 text-purple-600" />
          How It Works
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            The account switching feature is built on a secure multi-tenancy architecture:
          </p>

          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Mail className="h-4 w-4 mr-2 text-purple-600" />
                Email-Based Account Lookup
              </h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                The system identifies all client accounts associated with your email address and displays them
                in the account switcher interface.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <Shield className="h-4 w-4 mr-2 text-purple-600" />
                Secure Authentication
              </h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                When you switch accounts, the system generates a new authentication token for the selected client
                account while maintaining your session security.
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                <RefreshCw className="h-4 w-4 mr-2 text-purple-600" />
                Seamless Context Switching
              </h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm">
                Your user data, permissions, and preferences are automatically updated to match the selected
                account's context without requiring re-authentication.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notes */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <AlertCircle className="h-6 w-6 mr-2 text-amber-600" />
          Important Notes
        </h2>
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-6">
          <ul className="space-y-3">
            <li className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Role and Permissions:</span> Your role and permissions may differ
                across accounts. Always verify you have the necessary permissions in the current account.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Inactive Accounts:</span> You cannot switch to inactive accounts.
                Contact your administrator if you need access to a deactivated account.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Data Isolation:</span> Each client account has completely isolated
                data. Leads, campaigns, and other resources are not shared between accounts.
              </div>
            </li>
            <li className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Session Management:</span> Switching accounts updates your session
                context but does not log you out. You remain authenticated across all your accounts.
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* Use Cases */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Common Use Cases</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="bg-purple-100 dark:bg-purple-900/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Agency Management</h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              Marketing agencies can manage multiple client accounts using a single email, switching between
              clients to handle leads, campaigns, and communications.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="bg-purple-100 dark:bg-purple-900/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Building2 className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Multi-Department Access</h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              Enterprise users can switch between different business units or departments that operate as
              separate client accounts within the CRM.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="bg-purple-100 dark:bg-purple-900/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Consultant Access</h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              Business consultants working with multiple organizations can easily switch between client
              accounts to provide support and manage their CRM systems.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="bg-purple-100 dark:bg-purple-900/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Globe className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Regional Operations</h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm">
              Companies with regional offices set up as separate accounts can have managers switch between
              regions to monitor and support operations.
            </p>
          </div>
        </div>
      </section>

      {/* Need Help */}
      <section className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/10 dark:to-blue-900/10 rounded-lg p-6 border border-purple-200 dark:border-purple-800">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
          <Info className="h-5 w-5 mr-2 text-purple-600" />
          Need Help?
        </h3>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          If you're having trouble switching accounts or believe you should have access to an account that
          doesn't appear in your list, please contact your system administrator or support team.
        </p>
        <a
          href="mailto:support@yourcrm.com"
          className="inline-flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
        >
          <Mail className="h-4 w-4 mr-2" />
          Contact Support
        </a>
      </section>
    </div>
  );
};

export default AccountSwitchingDocs;
